import {MaybeRefOrGetter, onBeforeUnmount, toValue, watch} from 'vue'
import {App, BackButtonListenerEvent} from '@capacitor/app'
import {Capacitor, PluginListenerHandle} from '@capacitor/core'

interface INativeBackButtonHandler {
  id: symbol
  onBack: () => void
}

const handlers: INativeBackButtonHandler[] = []
let listenerHandlePromise: Promise<PluginListenerHandle> | null = null

function onBackButton({canGoBack}: BackButtonListenerEvent) {
  const handler = handlers.at(-1)
  if(handler) {
    handler.onBack()
    return
  }
  if(canGoBack) {
    window.history.back()
  }
}

function addHandler(handler: INativeBackButtonHandler) {
  handlers.push(handler)
  listenerHandlePromise ??= App.addListener('backButton', onBackButton)
}

function removeHandler(id: symbol) {
  const index = handlers.findIndex(handler => handler.id === id)
  if(index === -1) {
    return
  }
  handlers.splice(index, 1)
  if(handlers.length > 0 || !listenerHandlePromise) {
    return
  }
  const handlePromise = listenerHandlePromise
  listenerHandlePromise = null
  handlePromise.then(handle => handle.remove()).catch(() => undefined)
}

export function useNativeBackButton(isActive: MaybeRefOrGetter<boolean>, onBack: () => void) {
  if(!Capacitor.isNativePlatform()) {
    return
  }
  const id = Symbol()

  watch(() => toValue(isActive), (active) => {
    if(active) {
      addHandler({
        id,
        onBack,
      })
    } else {
      removeHandler(id)
    }
  }, {immediate: true})

  onBeforeUnmount(() => {
    removeHandler(id)
  })
}
